<template>
  <div
    class="tutorial-tooltip"
    :class="[`position-${computedPosition}`]"
    :style="tooltipStyle"
    role="dialog"
    aria-modal="true"
  >
    <!-- Arrow -->
    <div class="tooltip-arrow" :class="[`arrow-${computedPosition}`]" :style="arrowStyle" />

    <!-- Content -->
    <div class="tooltip-content">
      <h3 class="tooltip-title">{{ title }}</h3>
      <p class="tooltip-text">{{ content }}</p>
    </div>

    <!-- Navigation -->
    <div class="tooltip-nav" v-if="showPrev || showNext">
      <button
        v-if="showPrev"
        class="nav-btn prev-btn"
        @click="$emit('prev')"
      >
        上一步
      </button>
      <div v-else class="nav-spacer" />

      <button
        v-if="showNext"
        class="nav-btn next-btn"
        @click="$emit('next')"
      >
        {{ isLastStep ? '完成' : '下一步' }}
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TutorialTooltip',

  props: {
    title: {
      type: String,
      required: true
    },
    content: {
      type: String,
      required: true
    },
    position: {
      type: String,
      default: 'bottom',
      validator: (value) => ['top', 'bottom', 'left', 'right'].includes(value)
    },
    targetRect: {
      type: Object,
      default: null
    },
    showPrev: {
      type: Boolean,
      default: true
    },
    showNext: {
      type: Boolean,
      default: true
    },
    isLastStep: {
      type: Boolean,
      default: false
    },
    tooltipOffset: {
      type: Object,
      default: () => ({ top: 0, left: 0 })
    }
  },

  emits: ['prev', 'next'],

  data() {
    return {
      tooltipWidth: 280,
      tooltipHeight: 0,
      computedPosition: this.position
    };
  },

  computed: {
    tooltipStyle() {
      if (!this.targetRect) {
        return {
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)'
        };
      }

      const { originalTop, originalLeft, originalWidth, originalHeight } = this.targetRect;
      const padding = 16; // Gap between tooltip and target
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let top, left;
      let position = this.position;

      // Calculate position based on requested position
      switch (position) {
        case 'bottom':
          top = originalTop + originalHeight + padding;
          left = originalLeft + originalWidth / 2 - this.tooltipWidth / 2;
          // Check if tooltip goes off screen bottom
          if (top + 150 > viewportHeight) {
            position = 'top';
            top = originalTop - padding - 150;
          }
          break;
        case 'top':
          top = originalTop - padding - 150;
          left = originalLeft + originalWidth / 2 - this.tooltipWidth / 2;
          // Check if tooltip goes off screen top
          if (top < 0) {
            position = 'bottom';
            top = originalTop + originalHeight + padding;
          }
          break;
        case 'left':
          top = originalTop + originalHeight / 2 - 75;
          left = originalLeft - padding - this.tooltipWidth;
          // Check if tooltip goes off screen left
          if (left < 0) {
            position = 'right';
            left = originalLeft + originalWidth + padding;
          }
          break;
        case 'right':
          top = originalTop + originalHeight / 2 - 75;
          left = originalLeft + originalWidth + padding;
          // Check if tooltip goes off screen right
          if (left + this.tooltipWidth > viewportWidth) {
            position = 'left';
            left = originalLeft - padding - this.tooltipWidth;
          }
          break;
      }

      // Ensure tooltip stays within viewport horizontally
      if (left < 10) left = 10;
      if (left + this.tooltipWidth > viewportWidth - 10) {
        left = viewportWidth - this.tooltipWidth - 10;
      }

      // Ensure tooltip stays within viewport vertically
      if (top < 10) top = 10;
      if (top + 150 > viewportHeight - 10) {
        top = viewportHeight - 150 - 10;
      }

      // Apply custom offset
      const offsetTop = this.tooltipOffset && this.tooltipOffset.top ? this.tooltipOffset.top : 0;
      const offsetLeft = this.tooltipOffset && this.tooltipOffset.left ? this.tooltipOffset.left : 0;
      top += offsetTop;
      left += offsetLeft;

      // Update computed position for arrow
      this.$nextTick(() => {
        this.computedPosition = position;
      });

      return {
        top: `${top}px`,
        left: `${left}px`,
        width: `${this.tooltipWidth}px`
      };
    },

    arrowStyle() {
      // Calculate arrow position to point at target center
      if (!this.targetRect) {
        return { left: '50%', transform: 'translateX(-50%)' };
      }

      const position = this.computedPosition;
      const { originalLeft, originalWidth } = this.targetRect;

      // For top/bottom positions, calculate horizontal arrow position
      if (position === 'top' || position === 'bottom') {
        // Recalculate tooltip left position (same logic as tooltipStyle)
        const viewportWidth = window.innerWidth;
        let tooltipLeft = originalLeft + originalWidth / 2 - this.tooltipWidth / 2;

        // Apply bounds checking
        if (tooltipLeft < 10) tooltipLeft = 10;
        if (tooltipLeft + this.tooltipWidth > viewportWidth - 10) {
          tooltipLeft = viewportWidth - this.tooltipWidth - 10;
        }

        // Apply offset
        const offsetLeft = this.tooltipOffset && this.tooltipOffset.left ? this.tooltipOffset.left : 0;
        tooltipLeft += offsetLeft;

        // Calculate target center and arrow position
        const targetCenterX = originalLeft + originalWidth / 2;
        const arrowLeft = targetCenterX - tooltipLeft;

        // Clamp arrow position within tooltip bounds (with some padding)
        const minLeft = 20;
        const maxLeft = this.tooltipWidth - 20;
        const clampedLeft = Math.max(minLeft, Math.min(maxLeft, arrowLeft));

        return {
          left: `${clampedLeft}px`,
          transform: 'translateX(-50%)'
        };
      }

      return { left: '50%', transform: 'translateX(-50%)' };
    }
  },

  watch: {
    position: {
      immediate: true,
      handler(newVal) {
        this.computedPosition = newVal;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.tutorial-tooltip {
  position: fixed;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);
  padding: 16px 20px;
  z-index: 100005;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

// Arrow styles
.tooltip-arrow {
  position: absolute;
  width: 0;
  height: 0;
  border: 10px solid transparent;

  &.arrow-bottom {
    top: -20px;
    border-bottom-color: #ffffff;
  }

  &.arrow-top {
    bottom: -20px;
    border-top-color: #ffffff;
  }

  &.arrow-left {
    right: -20px;
    top: 50%;
    transform: translateY(-50%);
    border-left-color: #ffffff;
  }

  &.arrow-right {
    left: -20px;
    top: 50%;
    transform: translateY(-50%);
    border-right-color: #ffffff;
  }
}

.tooltip-content {
  margin-bottom: 16px;
}

.tooltip-title {
  font-size: 18px;
  font-weight: 600;
  color: #333333;
  margin: 0 0 8px 0;
}

.tooltip-text {
  font-size: 14px;
  color: #666666;
  line-height: 1.5;
  margin: 0;
}

.tooltip-nav {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.nav-spacer {
  flex: 1;
}

.nav-btn {
  padding: 8px 20px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &.prev-btn {
    background: transparent;
    border: 1px solid #cccccc;
    color: #666666;

    &:hover {
      border-color: #999999;
      color: #333333;
    }
  }

  &.next-btn {
    background: #4a90d9;
    border: none;
    color: #ffffff;

    &:hover {
      background: #357abd;
    }
  }
}
</style>
